import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { DefectSearchParams } from '../../../../../models/inspection';
import { InspectionService } from '../../../../../services/api/inspection/inspection.service';
import { IdbService } from '../../../../../services/storage/idb.service';
import { BridgeConstants } from '../../../../../shared/configs/bridge-constants.config';

import { Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs/operators';

@Component({
  selector: 'cube-new-defect-dialog',
  templateUrl: './new-defect-dialog.component.html',
  styleUrls: ['./new-defect-dialog.component.scss'],
  standalone: false,
})
export class NewDefectDialogComponent implements OnInit, OnDestroy {
  bridgeConstants: typeof BridgeConstants = BridgeConstants;
  defectForm: FormGroup;
  defectList: any[] = [];
  defectType: string;
  editData: any;
  elementoList: Array<string>;
  filteredDefectList: any[] = [];
  gValueOptions: Array<number> = [1, 2, 3, 4, 5];
  header: string;

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) private dialogData: any,
    private formBuilder: FormBuilder,
    private idbService: IdbService,
    private inspectionService: InspectionService,
    private matDialogRef: MatDialogRef<NewDefectDialogComponent>
  ) {
    this.defectForm = this.createFormGroup();
    this.defectType = this.dialogData.defectType;
    this.elementoList = this.dialogData.elementoList;
    this.editData = this.dialogData.editData;
  }

  ngOnInit(): void {
    switch (this.defectType) {
      case BridgeConstants.defectDialog.addDefect:
        this.header = 'Add Defect';
        break;

      case BridgeConstants.defectDialog.editDefect:
        this.header = 'Edit Defect';
        this.setDefectForm(this.editData);
        this.onSelectTipologiaDifetto(true);
        break;
    }
  }

  ngAfterViewInit(): void {
    this.setEventListeners();
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClickDefect(defect): void {
    const tipologiaDifetto = this.defectForm.get(['tipologiaDifetto'])?.value;
    this.setDefectForm({
      ...defect,
      tipologiaDifetto,
    });
  }

  onClickDelete(): void {
    this.matDialogRef.close({
      data: this.defectForm.getRawValue(),
      type: BridgeConstants.defectDialog.deleteDefect,
    });
  }

  onSelectTipologiaDifetto(isEdit: boolean = false): void {
    const tipologiaDifetto = this.defectForm.get(['tipologiaDifetto'])?.value;
    // Resetting form if not in edit mode for the first time
    if (!isEdit) {
      this.defectForm.reset({ tipologiaDifetto: tipologiaDifetto });
    }

    this.inspectionService.getOnlineStatus().then((isOnline) => {
      if (isOnline) {
        this.fetchDefectsList(tipologiaDifetto);
      } else {
        this.fetchDefectsListFromIndexedDb(tipologiaDifetto);
      }
    });
  }

  onSubmit(): void {
    this.matDialogRef.close({
      data: {
        ...this.defectForm.getRawValue(),
        tipologiaDifetto: this.defectForm.get(['tipologiaDifetto'])?.value,
      },
      type: this.defectType,
    });
  }

  private createFormGroup(): FormGroup {
    return this.formBuilder.group({
      codice_descrizione_difetto: [null],
      codice_difetto: [null, [Validators.required]],
      descrizione_difetto: [null, [Validators.required]],
      G: {
        value: null,
        disabled: true,
      },
      k1_1: { value: false, disabled: true },
      k1_2: { value: false, disabled: true },
      k1_3: { value: false, disabled: true },
      k2_1: { value: false, disabled: true },
      k2_2: { value: false, disabled: true },
      k2_3: { value: false, disabled: true },
      tipologiaDifetto: [null, [Validators.required, Validators.min(0)]],
    });
  }

  private fetchDefectsList(elemento: string): void {
    const formData: any = { elemento };

    this.inspectionService
      .getBridgeDefects(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.setDefectsList(res.data);
      });
  }

  private async fetchDefectsListFromIndexedDb(elemento: string): Promise<void> {
    const defectSearchParams: DefectSearchParams = { elemento };

    const defectsList = await this.idbService.getBridgeDefectsList(
      defectSearchParams
    );
    this.setDefectsList(defectsList);
  }

  private setDefectForm(defectValue): void {
    this.defectForm.patchValue({
      codice_difetto: defectValue['Codice difetto'],
      descrizione_difetto: defectValue['Descrizione difetto'],
      G: JSON.parse(defectValue['G']),
      k1_1: defectValue['K1 options']['0.2'],
      k1_2: defectValue['K1 options']['0.5'],
      k1_3: defectValue['K1 options']['1'],
      k2_1: defectValue['K2 options']['0.2'],
      k2_2: defectValue['K2 options']['0.5'],
      k2_3: defectValue['K2 options']['1'],
      tipologiaDifetto: defectValue.tipologiaDifetto,
    });
  }

  private setDefectsList(defectsList: any[]): void {
    // Adding unique defects
    const consolidatedDefectsList = [];
    defectsList.forEach((defect) => {
      if (
        !defect['Codice difetto'] ||
        defect['Codice difetto'] === BridgeConstants.eventualiNote
      )
        return;

      const isDefectAlreadyAdded = consolidatedDefectsList.some(
        (item) =>
          item['Codice difetto'] === defect['Codice difetto'] &&
          item['Descrizione difetto'] === defect['Descrizione difetto']
      );
      if (isDefectAlreadyAdded) return;

      consolidatedDefectsList.push(defect);
    });

    // Setting defects
    this.defectList = this.sortDefects(consolidatedDefectsList);
    this.filteredDefectList = [...this.defectList];
  }

  private setEventListeners(): void {
    this.defectForm
      .get(['codice_descrizione_difetto'])
      ?.valueChanges.pipe(
        map((searchText) => (searchText || '').toLowerCase()),
        debounceTime(250),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((searchText: string) => {
        this.filteredDefectList = this.defectList.filter((defect: any) =>
          `${defect['Codice difetto']} - ${defect['Descrizione difetto']}`
            .toLowerCase()
            .includes(searchText)
        );
      });
  }

  private sortDefects(defectsList: any[]): any[] {
    defectsList.sort((defect1, defect2) => {
      const name1 =
        `${defect1['Codice difetto']} - ${defect1['Descrizione difetto']}`.toLowerCase();
      const name2 =
        `${defect2['Codice difetto']} - ${defect2['Descrizione difetto']}`.toLowerCase();

      const name1Arr = name1.split(/(\d+)/);
      const name2Arr = name2.split(/(\d+)/);

      // Compare the corresponding substrings of the two strings
      for (let i = 0; i < Math.min(name1Arr.length, name2Arr.length); i++) {
        const name1Sub = name1Arr[i];
        const name2Sub = name2Arr[i];

        const name1SubNum = parseInt(name1Sub);
        const name2SubNum = parseInt(name2Sub);

        if (!isNaN(name1SubNum) && !isNaN(name2SubNum)) {
          // If both substrings are numbers, compare them numerically
          const diff = name1SubNum - name2SubNum;
          if (diff !== 0) return diff;
        } else {
          // Else compare the substrings alphabetically
          const cmp = name1Sub.localeCompare(name2Sub);
          if (cmp !== 0) return cmp;
        }
      }

      // If the substrings are equal up to this point, sort by string length
      return name1.length - name2.length;
    });

    return defectsList;
  }
}
